import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "../../i18n";
import { MaskLine, FadeUp } from "./Reveal";
import { MapPin, Clock, Phone, Mail, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, type = "text", value, onChange, required, textarea }) => (
  <div className="relative">
    <label htmlFor={id} className="font-mono-p text-xs uppercase tracking-widest text-white/40">{label}</label>
    {textarea ? (
      <textarea id={id} data-testid={`contact-${id}`} value={value} onChange={onChange} required={required} rows={3}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-3 text-lg resize-none transition-colors duration-300" />
    ) : (
      <input id={id} data-testid={`contact-${id}`} type={type} value={value} onChange={onChange} required={required}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-3 text-lg transition-colors duration-300" />
    )}
  </div>
);

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success(c.ok);
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error(c.err);
    } finally {
      setLoading(false);
    }
  };

  const infos = [
    { icon: MapPin, t: c.addressT, lines: c.address },
    { icon: Clock, t: c.hoursT, lines: c.hours },
    { icon: Phone, t: c.contactT, lines: ["+33 749 850 244", "info@fixedrone.com"] },
  ];

  return (
    <section id="contact" className="py-24 md:py-40 bg-[#070708] border-t border-white/10">
      <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-6">
            <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{c.tag}</span></FadeUp>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-6">
              <MaskLine>{c.title}</MaskLine>
            </h2>
            <FadeUp delay={0.1}><p className="text-white/60 mt-6 max-w-md leading-relaxed">{c.lead}</p></FadeUp>

            <form onSubmit={submit} data-testid="contact-form" className="mt-12 space-y-8 max-w-lg">
              <div className="grid sm:grid-cols-2 gap-8">
                <Field id="name" label={c.name} value={form.name} onChange={set("name")} required />
                <Field id="email" label={c.email} type="email" value={form.email} onChange={set("email")} required />
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field id="company" label={c.company} value={form.company} onChange={set("company")} />
                <Field id="subject" label={c.subject} value={form.subject} onChange={set("subject")} />
              </div>
              <Field id="message" label={c.message} value={form.message} onChange={set("message")} required textarea />
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-[var(--primary)] text-black font-medium px-8 py-4 rounded-full hover:bg-white transition-colors duration-300 disabled:opacity-60">
                {done ? <><Check size={18} /> {c.ok.split(".")[0]}</> : <>{loading ? c.sending : c.send} <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" /></>}
              </button>
            </form>
          </div>

          <div className="lg:col-span-6 lg:pl-10 lg:border-l border-white/10">
            <div className="space-y-10">
              {infos.map((info) => (
                <FadeUp key={info.t} delay={0.05}>
                  <div className="flex gap-5">
                    <span className="shrink-0 w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-primary-c">
                      <info.icon size={18} />
                    </span>
                    <div>
                      <h4 className="font-mono-p text-xs uppercase tracking-widest text-white/40 mb-2">{info.t}</h4>
                      {info.lines.map((l) => (<p key={l} className="text-lg text-white/85 leading-snug">{l}</p>))}
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="mt-14 relative overflow-hidden clip-frame border border-white/10 h-56">
              <iframe title="map" className="w-full h-full grayscale invert-[0.9] contrast-[1.1]" loading="lazy"
                src="https://www.google.com/maps?q=6D%20Route%20de%20Tr%C3%A8ves%2C%20L-2633%20Senningerberg%2C%20Luxembourg&z=14&output=embed" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
